import type * as Y from 'yjs';

export type LegacyArchiveXYWH = [number, number, number, number];

export type LegacyArchiveSerializedXYWH =
  `[${number},${number},${number},${number}]`;

export type LegacyYDocUpgradeDiagnosticReason =
  | 'missing'
  | 'invalid'
  | 'nested-non-canvas'
  | 'zero-size';

export type LegacyYDocUpgradeDiagnosticScope = 'block' | 'surface-element';
export type LegacyYDocUpgradeDiagnosticAction = 'drop' | 'repair';

export type LegacyYDocUpgradeDiagnostic = {
  readonly action: LegacyYDocUpgradeDiagnosticAction;
  readonly blockId?: string;
  readonly docId?: string;
  readonly elementId?: string;
  readonly elementType?: string;
  readonly flavour?: string;
  readonly parentFlavour?: string;
  readonly parentId?: string;
  readonly persistedValue: unknown;
  readonly reason: LegacyYDocUpgradeDiagnosticReason;
  readonly repairedValue?: LegacyArchiveSerializedXYWH;
  readonly scope: LegacyYDocUpgradeDiagnosticScope;
  readonly source?: string;
  readonly sourceDocId?: string;
  readonly upgrade: 'canvas-geometry-v1';
};

export type UpgradeLegacyDocSnapshotResult = {
  readonly changed: boolean;
  readonly diagnostics: readonly LegacyYDocUpgradeDiagnostic[];
};

export type LegacyDocSnapshotLike = {
  readonly blocks?: LegacyBlockSnapshotLike;
  readonly meta?: { readonly id?: unknown };
};

export type UpgradeLegacyDocSnapshotInput = {
  readonly docId?: string;
  readonly onDiagnostic?: (diagnostic: LegacyYDocUpgradeDiagnostic) => void;
  readonly snapshot: LegacyDocSnapshotLike;
  readonly source?: string;
  readonly sourceDocId?: string;
  readonly systemDoc?: boolean;
};

type LegacyBlockSnapshotLike = {
  readonly children?: LegacyBlockSnapshotLike[];
  readonly flavour?: unknown;
  readonly id?: unknown;
  readonly props?: Record<string, unknown>;
};

type MutableXYWH = [number, number, number, number];

const CANVAS_BLOCK_FALLBACK_SIZE_BY_FLAVOUR = new Map<string, [number, number]>(
  [
    ['affine:note', [498, 92]],
    ['affine:frame', [800, 600]],
    ['affine:image', [100, 100]],
    ['affine:latex', [16, 16]],
    ['affine:database', [800, 300]],
    ['affine:attachment', [240, 84]],
    ['affine:bookmark', [240, 84]],
    ['affine:embed', [400, 300]],
    ['affine:embed-github', [400, 300]],
    ['affine:embed-html', [400, 300]],
    ['affine:embed-iframe', [400, 300]],
    ['affine:embed-loom', [400, 300]],
    ['affine:embed-synced-doc', [400, 300]],
    ['affine:embed-youtube', [400, 300]],
    ['affine:edgeless-text', [160, 40]],
    ['affine:surface-ref', [400, 300]],
    ['affine:automation', [480, 240]],
  ]
);

const SURFACE_ELEMENT_FALLBACK_SIZE_BY_TYPE = new Map<string, [number, number]>(
  [
    ['connector', [120, 16]],
    ['group', [100, 100]],
    ['mindmap', [240, 120]],
    ['shape', [100, 100]],
    ['text', [160, 40]],
  ]
);

const MIN_CONNECTOR_SIZE = 16;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isXYWH(value: unknown): value is LegacyArchiveXYWH {
  return (
    Array.isArray(value) && value.length === 4 && value.every(isFiniteNumber)
  );
}

export function parseLegacyArchiveXYWH(
  value: unknown
): LegacyArchiveXYWH | null {
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value) as unknown;
    return isXYWH(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function hasPositiveSize(xywh: LegacyArchiveXYWH): boolean {
  return xywh[2] > 0 && xywh[3] > 0;
}

export function serializeLegacyArchiveXYWH(
  xywh: LegacyArchiveXYWH
): LegacyArchiveSerializedXYWH {
  if (!isXYWH(xywh)) {
    throw new TypeError('xywh must be a finite [x,y,width,height] tuple');
  }
  if (!hasPositiveSize(xywh)) {
    throw new TypeError('xywh must have positive width and height');
  }

  return JSON.stringify(xywh) as LegacyArchiveSerializedXYWH;
}

function isYMap(value: unknown): value is Y.Map<unknown> {
  return (
    isRecord(value) &&
    typeof (value as { forEach?: unknown }).forEach === 'function' &&
    typeof (value as { get?: unknown }).get === 'function' &&
    typeof (value as { has?: unknown }).has === 'function' &&
    typeof (value as { set?: unknown }).set === 'function'
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// The upgrader depends on jszip and yjs only; these mirror NATIVE_UNIQ_IDENTIFIER
// (blocksuite/framework/store/src/consts.ts) and SURFACE_YMAP_UNIQ_IDENTIFIER
// (blocksuite/framework/std/src/gfx/model/surface/surface-model.ts).
const NATIVE_SNAPSHOT_MARKER = '$blocksuite:internal:native$';
const SURFACE_YMAP_SNAPSHOT_MARKER = 'affine:surface:ymap';

/**
 * Surface elements of a doc snapshot: boxed as a native value by the store
 * transformer, or the plain element map that older snapshot archives wrote.
 */
function snapshotSurfaceElements(
  value: unknown
): Record<string, unknown> | null {
  if (!isRecord(value)) return null;
  if (value[NATIVE_SNAPSHOT_MARKER] === true) {
    return isRecord(value.value) ? value.value : null;
  }
  return value;
}

function readObjectProperty(value: unknown, key: string): unknown {
  if (isYMap(value)) return value.get(key);
  if (isRecord(value)) return value[key];
  return undefined;
}

function readPoint(value: unknown): [number, number] | null {
  if (
    Array.isArray(value) &&
    value.length === 2 &&
    isFiniteNumber(value[0]) &&
    isFiniteNumber(value[1])
  ) {
    return [value[0], value[1]];
  }

  if (typeof value === 'string') {
    try {
      return readPoint(JSON.parse(value) as unknown);
    } catch {
      return null;
    }
  }

  if (isRecord(value)) {
    const x = value.x;
    const y = value.y;
    if (isFiniteNumber(x) && isFiniteNumber(y)) {
      return [x, y];
    }
  }

  return null;
}

function normalizeXYWH(
  value: unknown,
  fallback: LegacyArchiveXYWH
): {
  readonly reason: LegacyYDocUpgradeDiagnosticReason;
  readonly xywh: LegacyArchiveXYWH;
} | null {
  const parsed = parseLegacyArchiveXYWH(value);
  if (parsed && hasPositiveSize(parsed)) {
    return null;
  }

  if (parsed) {
    return {
      reason: 'zero-size',
      xywh: [
        parsed[0],
        parsed[1],
        parsed[2] > 0 ? parsed[2] : fallback[2],
        parsed[3] > 0 ? parsed[3] : fallback[3],
      ],
    };
  }

  return {
    reason: value === undefined || value === null ? 'missing' : 'invalid',
    xywh: fallback,
  };
}

function fallbackXYWH(
  value: unknown,
  size: readonly [number, number]
): LegacyArchiveXYWH {
  const parsed = parseLegacyArchiveXYWH(value);
  return [parsed?.[0] ?? 0, parsed?.[1] ?? 0, size[0], size[1]];
}

function unionBounds(
  current: MutableXYWH | null,
  next: LegacyArchiveXYWH
): MutableXYWH {
  if (!current) {
    return [next[0], next[1], next[2], next[3]];
  }

  const minX = Math.min(current[0], next[0]);
  const minY = Math.min(current[1], next[1]);
  const maxX = Math.max(current[0] + current[2], next[0] + next[2]);
  const maxY = Math.max(current[1] + current[3], next[1] + next[3]);
  return [minX, minY, maxX - minX, maxY - minY];
}

function padBound(
  bound: LegacyArchiveXYWH,
  padding: number
): LegacyArchiveXYWH {
  return [
    bound[0] - padding,
    bound[1] - padding,
    Math.max(MIN_CONNECTOR_SIZE, bound[2] + padding * 2),
    Math.max(MIN_CONNECTOR_SIZE, bound[3] + padding * 2),
  ];
}

function collectChildIds(value: unknown): string[] {
  const ids: string[] = [];
  if (isYMap(value)) {
    value.forEach((childValue, key) => {
      if (childValue !== false) ids.push(key);
    });
    return ids;
  }

  const record = surfaceYMapJSON(value);
  if (record) {
    for (const [key, childValue] of Object.entries(record)) {
      if (childValue !== false) ids.push(key);
    }
  }

  return ids;
}

/** A snapshot writes a surface Y.Map as `{ 'affine:surface:ymap': true, json }`. */
function surfaceYMapJSON(value: unknown): Record<string, unknown> | null {
  if (!isRecord(value)) return null;
  if (value[SURFACE_YMAP_SNAPSHOT_MARKER] === true) {
    return isRecord(value.json) ? value.json : null;
  }
  return value;
}

function collectSnapshotBlocks(
  block: LegacyBlockSnapshotLike | undefined,
  callback: (
    block: LegacyBlockSnapshotLike,
    parent: LegacyBlockSnapshotLike | undefined
  ) => void,
  parent?: LegacyBlockSnapshotLike
) {
  if (!block) return;
  callback(block, parent);
  for (const child of block.children ?? []) {
    collectSnapshotBlocks(child, callback, block);
  }
}

function isCanvasRenderedBlockParent(parentFlavour: string | undefined) {
  return parentFlavour === 'affine:page' || parentFlavour === 'affine:surface';
}

function isLikelyDefaultNestedXYWH(
  flavour: string | undefined,
  xywh: LegacyArchiveXYWH | null
) {
  if (!flavour || !xywh) return false;
  const size = CANVAS_BLOCK_FALLBACK_SIZE_BY_FLAVOUR.get(flavour);
  return (
    !!size &&
    xywh[0] === 0 &&
    xywh[1] === 0 &&
    xywh[2] === size[0] &&
    xywh[3] === size[1]
  );
}

function shouldDropNestedNonCanvasXYWH(input: {
  readonly flavour?: string;
  readonly parentFlavour?: string;
  readonly xywhValue: unknown;
}) {
  if (isCanvasRenderedBlockParent(input.parentFlavour)) return false;
  const parsed = parseLegacyArchiveXYWH(input.xywhValue);
  return (
    !parsed ||
    !hasPositiveSize(parsed) ||
    isLikelyDefaultNestedXYWH(input.flavour, parsed)
  );
}

function readConnectionPoint(
  connection: unknown,
  resolveBound: (id: string) => LegacyArchiveXYWH | null
): [number, number] | null {
  const id = readObjectProperty(connection, 'id');
  const position = readPoint(readObjectProperty(connection, 'position'));

  if (typeof id === 'string') {
    const bound = resolveBound(id);
    if (!bound) return null;
    const [relativeX, relativeY] = position ?? [0.5, 0.5];
    return [bound[0] + bound[2] * relativeX, bound[1] + bound[3] * relativeY];
  }

  return position;
}

function upgradeLegacySnapshotGeometry(
  input: UpgradeLegacyDocSnapshotInput
): UpgradeLegacyDocSnapshotResult {
  const diagnostics: LegacyYDocUpgradeDiagnostic[] = [];
  const docId =
    input.docId ??
    (typeof input.snapshot.meta?.id === 'string'
      ? input.snapshot.meta.id
      : undefined);
  const blockBounds = new Map<string, LegacyArchiveXYWH>();
  const surfaceElements = new Map<string, Record<string, unknown>>();
  let changed = false;

  const recordDiagnostic = (
    diagnostic: Omit<
      LegacyYDocUpgradeDiagnostic,
      'docId' | 'source' | 'sourceDocId' | 'upgrade'
    >
  ) => {
    const fullDiagnostic: LegacyYDocUpgradeDiagnostic = {
      ...diagnostic,
      docId,
      source: input.source,
      sourceDocId: input.sourceDocId,
      upgrade: 'canvas-geometry-v1',
    };
    diagnostics.push(fullDiagnostic);
    input.onDiagnostic?.(fullDiagnostic);
  };

  collectSnapshotBlocks(input.snapshot.blocks, (block, parent) => {
    if (typeof block.id !== 'string' || typeof block.flavour !== 'string') {
      return;
    }
    const props = block.props;
    if (!props) return;

    const xywhValue = props.xywh;
    const size = CANVAS_BLOCK_FALLBACK_SIZE_BY_FLAVOUR.get(block.flavour);
    const parentFlavour =
      typeof parent?.flavour === 'string' ? parent.flavour : undefined;
    const parentId = typeof parent?.id === 'string' ? parent.id : undefined;
    const isCanvasRendered = isCanvasRenderedBlockParent(parentFlavour);

    if (
      Object.prototype.hasOwnProperty.call(props, 'xywh') &&
      !isCanvasRendered &&
      shouldDropNestedNonCanvasXYWH({
        flavour: block.flavour,
        parentFlavour,
        xywhValue,
      })
    ) {
      delete props.xywh;
      changed = true;
      recordDiagnostic({
        action: 'drop',
        blockId: block.id,
        flavour: block.flavour,
        parentFlavour,
        parentId,
        persistedValue: xywhValue,
        reason: 'nested-non-canvas',
        scope: 'block',
      });
      return;
    }

    const shouldRepair =
      isCanvasRendered &&
      (Object.prototype.hasOwnProperty.call(props, 'xywh') ||
        (!input.systemDoc && size !== undefined));

    if (shouldRepair && size) {
      const normalized = normalizeXYWH(
        xywhValue,
        fallbackXYWH(xywhValue, size)
      );
      if (normalized) {
        const repairedValue = serializeLegacyArchiveXYWH(normalized.xywh);
        props.xywh = repairedValue;
        changed = true;
        recordDiagnostic({
          action: 'repair',
          blockId: block.id,
          flavour: block.flavour,
          parentFlavour,
          parentId,
          persistedValue: xywhValue,
          reason: normalized.reason,
          repairedValue,
          scope: 'block',
        });
        blockBounds.set(block.id, normalized.xywh);
      } else {
        const parsed = parseLegacyArchiveXYWH(xywhValue);
        if (parsed) blockBounds.set(block.id, parsed);
      }
    }

    if (block.flavour !== 'affine:surface') return;
    const elements = snapshotSurfaceElements(props.elements);
    if (!elements) return;

    for (const [elementId, element] of Object.entries(elements)) {
      if (isRecord(element)) {
        surfaceElements.set(elementId, element);
      }
    }
  });

  const elementBounds = new Map<string, LegacyArchiveXYWH>();
  const resolving = new Set<string>();
  const resolveAnyBound = (id: string): LegacyArchiveXYWH | null =>
    blockBounds.get(id) ?? resolveElementBound(id);

  const computeConnectorBound = (
    element: Record<string, unknown>
  ): LegacyArchiveXYWH | null => {
    const sourcePoint = readConnectionPoint(element.source, resolveAnyBound);
    const targetPoint = readConnectionPoint(element.target, resolveAnyBound);
    let bound: MutableXYWH | null = null;

    for (const point of [sourcePoint, targetPoint]) {
      if (!point) continue;
      bound = unionBounds(bound, [point[0], point[1], 0, 0]);
    }

    if (Array.isArray(element.labelXYWH) && isXYWH(element.labelXYWH)) {
      bound = unionBounds(bound, element.labelXYWH);
    }

    if (!bound) return null;
    const strokeWidth = isFiniteNumber(element.strokeWidth)
      ? element.strokeWidth
      : 1;
    return padBound(bound, Math.max(8, strokeWidth * 2));
  };

  const computeChildrenBound = (
    element: Record<string, unknown>
  ): LegacyArchiveXYWH | null => {
    let bound: MutableXYWH | null = null;
    for (const childId of collectChildIds(element.children)) {
      const childBound = resolveAnyBound(childId);
      if (!childBound) continue;
      bound = unionBounds(bound, childBound);
    }
    return bound;
  };

  function resolveElementBound(id: string): LegacyArchiveXYWH | null {
    const existing = elementBounds.get(id);
    if (existing) return existing;

    const element = surfaceElements.get(id);
    if (!element || resolving.has(id)) return null;

    resolving.add(id);
    try {
      const type = typeof element.type === 'string' ? element.type : undefined;
      const xywhValue = element.xywh;
      const parsed = parseLegacyArchiveXYWH(xywhValue);
      if (parsed && hasPositiveSize(parsed)) {
        elementBounds.set(id, parsed);
        return parsed;
      }

      const size = type
        ? SURFACE_ELEMENT_FALLBACK_SIZE_BY_TYPE.get(type)
        : undefined;
      const computed =
        type === 'connector'
          ? computeConnectorBound(element)
          : type === 'group' || type === 'mindmap'
            ? computeChildrenBound(element)
            : null;
      const fallback = computed ?? fallbackXYWH(xywhValue, size ?? [100, 100]);
      const normalized = normalizeXYWH(xywhValue, fallback);
      if (!normalized) return null;

      const repairedValue = serializeLegacyArchiveXYWH(normalized.xywh);
      element.xywh = repairedValue;
      changed = true;
      elementBounds.set(id, normalized.xywh);
      recordDiagnostic({
        action: 'repair',
        elementId: id,
        elementType: type,
        persistedValue: xywhValue,
        reason: normalized.reason,
        repairedValue,
        scope: 'surface-element',
      });
      return normalized.xywh;
    } finally {
      resolving.delete(id);
    }
  }

  for (const elementId of surfaceElements.keys()) {
    resolveElementBound(elementId);
  }

  return { changed, diagnostics };
}

export function upgradeLegacyDocSnapshot(
  input: UpgradeLegacyDocSnapshotInput
): UpgradeLegacyDocSnapshotResult {
  return upgradeLegacySnapshotGeometry(input);
}
